import React, { useState, useEffect, useMemo } from 'react';
import './AdminPanel.css';
import { getUsers, saveUsers } from '../utils/storage';
import { getRankList, upsertCustomRank, deleteCustomRank } from '../utils/rankConfig';

// Used for creating/editing custom ranks
const initialRankForm = {
  id: '',
  name: '',
  color: '#aab1c7',
  canSendWorldImages: false,
  canSendPrivateImages: true,
};

function AdminPanel({ onClose, currentUser }) {
  // User management
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);

  // Ranks
  const [availableRanks, setAvailableRanks] = useState([]);
  const [rankForm, setRankForm] = useState(initialRankForm);
  const [editingRankId, setEditingRankId] = useState(null);
  const [rankMessage, setRankMessage] = useState('');

  // Account and password management
  const [newRole, setNewRole] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [newIP, setNewIP] = useState('');

  // Entry management
  const [entryData, setEntryData] = useState({
    giveawayId: '',
    entries: 1,
    duration: 7,
  });

  // Load users and ranks on mount
  useEffect(() => {
    loadUsers();
    loadRanks();
  }, []);

  // Giveaways management - use local state, update on storage change
  const [giveaways, setGiveaways] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('nitroGiveaways')) || [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    function updateGiveaways(e) {
      if (!e || !e.key || e.key === 'nitroGiveaways') {
        try {
          setGiveaways(JSON.parse(localStorage.getItem('nitroGiveaways')) || []);
        } catch {
          setGiveaways([]);
        }
      }
    }
    window.addEventListener('storage', updateGiveaways);
    updateGiveaways({});
    return () => window.removeEventListener('storage', updateGiveaways);
  }, []);

  function loadUsers() {
    try {
      setUsers(getUsers() || []);
    } catch {
      setUsers([]);
    }
  }

  function loadRanks() {
    setAvailableRanks(getRankList() || []);
  }

  function resetRankForm(clearMessage = true) {
    setRankForm(initialRankForm);
    setEditingRankId(null);
    if (clearMessage) setRankMessage('');
  }

  // Custom Ranks
  function handleSaveRankDefinition() {
    if (!rankForm.name.trim()) {
      alert('Rank must have a name.');
      return;
    }
    const rankId = rankForm.id
      ? rankForm.id.trim()
      : rankForm.name.trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9\-_]/gi, '');

    // Prevent duplicate rank id
    if (
      availableRanks.find(
        r =>
          (r.id === rankId && !editingRankId) ||
          (editingRankId && r.id === rankId && r.id !== editingRankId)
      )
    ) {
      alert('A rank with this identifier already exists.');
      return;
    }

    try {
      upsertCustomRank({
        id: rankId,
        name: rankForm.name.trim(),
        color: rankForm.color,
        permissions: {
          canSendWorldMessages: true,
          canSendWorldImages: rankForm.canSendWorldImages,
          canSendPrivateImages: rankForm.canSendPrivateImages,
        },
      });
      setRankMessage(`Saved rank "${rankForm.name.trim()}"`);
      resetRankForm(false);
      loadRanks();
    } catch (e) {
      alert(e.message || 'Error saving rank');
    }
  }

  function handleEditRank(rank) {
    setRankForm({
      id: rank.id,
      name: rank.name,
      color: rank.color,
      canSendWorldImages: !!rank.permissions?.canSendWorldImages,
      canSendPrivateImages: rank.permissions?.canSendPrivateImages !== false,
    });
    setEditingRankId(rank.id);
    setRankMessage('');
  }

  function handleDeleteRank(id) {
    if (!window.confirm('Delete this custom rank?')) return;
    try {
      deleteCustomRank(id);
      if (editingRankId === id) resetRankForm();
      loadRanks();
    } catch (e) {
      alert(e.message || 'Failed deleting custom rank');
    }
  }

  // Filtering users
  const filteredUsers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return users;
    return users.filter(
      u =>
        (u.discordUsername && u.discordUsername.toLowerCase().includes(term)) ||
        (u.email && u.email.toLowerCase().includes(term))
    );
  }, [users, searchTerm]);

  function handleUserSelect(user) {
    setSelectedUser(user);
    if (giveaways.length && !entryData.giveawayId) {
      setEntryData(prev => ({ ...prev, giveawayId: giveaways[0].id.toString() }));
    }
  }

  // Role/permission checks
  function canChangeRoles() {
    return currentUser?.role === 'founder' || currentUser?.role === 'co-founder';
  }
  function canChangePasswords() {
    return currentUser?.role === 'founder';
  }

  // User management actions

  function handleUpdateEntries() {
    if (!selectedUser || !entryData.giveawayId) return;
    const foundUser = users.find(u => u.id === selectedUser.id);
    if (!foundUser) return;

    const entries = { ...(foundUser.entries || {}) };
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + (parseInt(entryData.duration, 10) || 1));

    entries[entryData.giveawayId] = {
      count: parseInt(entryData.entries, 10) || 1,
      expiresAt: expiresAt.toISOString(),
      setBy: currentUser?.discordUsername || 'admin',
      setAt: new Date().toISOString(),
    };
    const updatedUsers = users.map(user =>
      user.id === selectedUser.id ? { ...user, entries } : user
    );
    setUsers(updatedUsers);
    saveUsers(updatedUsers);
    alert(`Updated ${selectedUser.discordUsername}'s entries for giveaway ${entryData.giveawayId}`);
    loadUsers();
    setSelectedUser(u => ({
      ...u,
      entries: { ...entries },
    }));
  }

  function handleChangeRole() {
    if (!selectedUser || !newRole) return;
    if (!canChangeRoles()) {
      alert('Only Founder and Co-Founder can change user roles.');
      return;
    }
    if (selectedUser.role === newRole) {
      alert(`User already has the role "${newRole}".`);
      return;
    }
    const updatedUsers = users.map(u =>
      u.id === selectedUser.id ? { ...u, role: newRole } : u
    );
    setUsers(updatedUsers);
    saveUsers(updatedUsers);
    alert(`Changed ${selectedUser.discordUsername}'s role to ${newRole}`);
    window.dispatchEvent(new Event('storage'));
    loadUsers();
    setSelectedUser(u => ({ ...u, role: newRole }));
    setNewRole('');
  }

  function handleChangePassword() {
    if (!selectedUser || !newPassword) return;
    if (!canChangePasswords()) {
      alert('Only Founder can change user passwords.');
      return;
    }
    if (newPassword.length < 6) {
      alert('Password must be at least 6 characters.');
      return;
    }
    const updatedUsers = users.map(
      u => (u.id === selectedUser.id ? { ...u, password: newPassword } : u)
    );
    setUsers(updatedUsers);
    saveUsers(updatedUsers);
    alert(`Password changed for ${selectedUser.discordUsername}`);
    setNewPassword('');
    setShowPasswordChange(false);
  }

  function handleLockAccount() {
    if (!selectedUser) return;
    if (selectedUser.locked) {
      alert('Account is already locked.');
      return;
    }
    const updatedUsers = users.map(user =>
      user.id === selectedUser.id
        ? {
            ...user,
            locked: true,
            lockedAt: new Date().toISOString(),
            lockedBy: currentUser?.discordUsername || 'admin',
          }
        : user
    );
    setUsers(updatedUsers);
    saveUsers(updatedUsers);
    alert(`Locked account: ${selectedUser.discordUsername}`);
    loadUsers();
    setSelectedUser(u => ({ ...u, locked: true }));
  }

  function handleUnlockAccount() {
    if (!selectedUser) return;
    if (!selectedUser.locked) {
      alert('Account not locked.');
      return;
    }
    const updatedUsers = users.map(user =>
      user.id === selectedUser.id
        ? (() => {
            const { locked, lockedAt, lockedBy, ...rest } = user;
            return rest;
          })()
        : user
    );
    setUsers(updatedUsers);
    saveUsers(updatedUsers);
    alert(`Unlocked account: ${selectedUser.discordUsername}`);
    loadUsers();
    const { locked, lockedAt, lockedBy, ...rest } = selectedUser;
    setSelectedUser(rest);
  }

  function handleDeleteAccount() {
    if (!selectedUser) return;
    if (
      !window.confirm(
        `Are you sure you want to DELETE the account "${selectedUser.discordUsername}"? This action cannot be undone!`
      )
    ) {
      return;
    }
    const updatedUsers = users.filter(u => u.id !== selectedUser.id);
    setUsers(updatedUsers);
    saveUsers(updatedUsers);
    alert(`Deleted account: ${selectedUser.discordUsername}`);
    setSelectedUser(null);
    loadUsers();
  }

  function handleAddIP() {
    if (!selectedUser || !newIP) return;
    const ipNorm = newIP.trim();
    if (!/^\d{1,3}(\.\d{1,3}){3}$/.test(ipNorm)) {
      alert('Please enter a valid IPv4 address.');
      return;
    }
    const updatedUsers = users.map(user => {
      if (user.id !== selectedUser.id) return user;
      let ips = Array.isArray(user.ips) ? [...user.ips] : [];
      if (!ips.includes(ipNorm)) ips.push(ipNorm);
      return { ...user, ips };
    });
    setUsers(updatedUsers);
    saveUsers(updatedUsers);
    alert(`Added IP ${ipNorm} to ${selectedUser.discordUsername}`);
    setNewIP('');
    loadUsers();
    setSelectedUser(updatedUsers.find(u => u.id === selectedUser.id));
  }

  function handleRemoveIP(ip) {
    if (!selectedUser) return;
    const updatedUsers = users.map(user =>
      user.id === selectedUser.id
        ? {
            ...user,
            ips: (user.ips || []).filter(i => i !== ip),
          }
        : user
    );
    setUsers(updatedUsers);
    saveUsers(updatedUsers);
    alert(`Removed IP ${ip} from ${selectedUser.discordUsername}`);
    loadUsers();
    setSelectedUser(updatedUsers.find(u => u.id === selectedUser.id));
  }


  // Render
  return (
    <div className="admin-overlay" onClick={onClose} tabIndex={-1}>
      <div className="admin-panel" onClick={e => e.stopPropagation()}>
        <div className="admin-header">
          <h2>🔧 Admin Panel</h2>
          <div className="admin-user-info">
            <span className="user-role">{currentUser?.role || 'Admin'}</span>
            <button className="close-btn" onClick={onClose} title="Close panel">
              ×
            </button>
          </div>
        </div>
        <div className="admin-search">
          <input
            type="text"
            placeholder="Search by Discord username or email..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="search-input"
            spellCheck="false"
          />
        </div>
        <div className="admin-content">
          {/* User List */}
          <div className="users-list">
            <h3>Users ({filteredUsers.length})</h3>
            <div className="users-scroll">
              {filteredUsers.length === 0 && (
                <div className="user-item user-item-empty">No users found.</div>
              )}
              {filteredUsers.map(user => (
                <div
                  key={user.id}
                  className={`user-item ${selectedUser?.id === user.id ? 'selected' : ''}`}
                  onClick={() => handleUserSelect(user)}
                  tabIndex={0}
                >
                  <div className="user-info">
                    <strong>{user.discordUsername || user.email || user.id}</strong>
                    <small>{user.email}</small>
                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: 4, flexWrap: 'wrap' }}>
                      {user.role === 'founder' && <span className="badge founder">Founder</span>}
                      {user.role === 'co-founder' && <span className="badge cofounder">Co-Founder</span>}
                      {user.locked && <span className="badge locked">🔒 Locked</span>}
                      {user.ips && user.ips.length > 1 && (
                        <span className="badge multiip" title={user.ips.length + " IP addresses"}>
                          {user.ips.length} IPs
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* User Details */}
          {selectedUser && (
            <div className="user-details">
              <h3>User Details: {selectedUser.discordUsername || selectedUser.email}</h3>
              <div className="user-stats">
                <div className="stat">
                  <label>Email:</label>
                  <span>{selectedUser.email}</span>
                </div>
                <div className="stat">
                  <label>Role:</label>
                  <span>{selectedUser.role}</span>
                </div>
                <div className="stat">
                  <label>Joined:</label>
                  <span>
                    {selectedUser.createdAt
                      ? new Date(selectedUser.createdAt).toLocaleDateString()
                      : '---'}
                  </span>
                </div>
                <div className="stat">
                  <label>Status:</label>
                  <span style={{ color: selectedUser.locked ? '#ff4d6d' : '#10de82' }}>
                    {selectedUser.locked ? '🔒 Locked' : '✓ Active'}
                  </span>
                </div>
                {selectedUser.locked && selectedUser.lockedAt && (
                  <div className="stat">
                    <label>Locked:</label>
                    <span>{new Date(selectedUser.lockedAt).toLocaleDateString()}</span>
                  </div>
                )}
              </div>

              {/* IP Management */}
              <div className="ip-management">
                <h4>IP Addresses</h4>
                <div className="ip-list">
                  {selectedUser.ips && selectedUser.ips.length > 0 ? (
                    selectedUser.ips.map((ip, idx) => (
                      <div key={ip + idx} className="ip-item">
                        <span>{ip}</span>
                        {selectedUser.lastIP === ip && <span className="last-ip">(Current)</span>}
                        <button
                          className="remove-ip-btn"
                          onClick={() => handleRemoveIP(ip)}
                          type="button"
                          title="Remove IP"
                          aria-label={`Remove IP ${ip}`}
                        >
                          ×
                        </button>
                      </div>
                    ))
                  ) : (
                    <p>No IPs recorded</p>
                  )}
                </div>
                <div className="form-group">
                  <input
                    type="text"
                    placeholder="Add IP address..."
                    value={newIP}
                    onChange={e => setNewIP(e.target.value)}
                  />
                  <button
                    className="add-ip-btn"
                    onClick={handleAddIP}
                    type="button"
                    disabled={!newIP.trim()}
                  >
                    Add IP
                  </button>
                </div>
              </div>

              {/* Role Management */}
              {canChangeRoles() && (
                <div className="role-management">
                  <h4>Change Role</h4>
                  <div className="form-group">
                    <select
                      value={newRole}
                      onChange={e => setNewRole(e.target.value)}
                      disabled={availableRanks.length === 0}
                    >
                      <option value="">Select new role...</option>
                      {availableRanks
                        .filter(r => r.id !== 'system')
                        .map(r => (
                          <option key={r.id} value={r.id}>
                            {r.name}{r.isDefault ? '' : ' (custom)'}
                          </option>
                        ))}
                    </select>
                    <button
                      className="update-btn"
                      type="button"
                      onClick={handleChangeRole}
                      disabled={!newRole || selectedUser.role === newRole}
                    >
                      Change Role
                    </button>
                  </div>
                </div>
              )}

              {/* Custom Ranks – Founder only */}
              {currentUser?.role === 'founder' && (
                <div className="custom-rank-management">
                  <h4>Custom Ranks</h4>
                  <div className="form-group">
                    <label>Rank Name</label>
                    <input
                      type="text"
                      placeholder="e.g. Elite Mod"
                      value={rankForm.name}
                      onChange={e => setRankForm({ ...rankForm, name: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Identifier (optional)</label>
                    <input
                      type="text"
                      placeholder="internal-id"
                      value={rankForm.id}
                      onChange={e => setRankForm({ ...rankForm, id: e.target.value })}
                    />
                  </div>
                  <div className="form-group color-input">
                    <label>Badge Color</label>
                    <input
                      type="color"
                      value={rankForm.color}
                      onChange={e => setRankForm({ ...rankForm, color: e.target.value })}
                    />
                  </div>
                  <div className="rank-permissions">
                    <label>
                      <input
                        type="checkbox"
                        checked={rankForm.canSendWorldImages}
                        onChange={e => setRankForm({ ...rankForm, canSendWorldImages: e.target.checked })}
                      />
                      Allow world chat images
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        checked={rankForm.canSendPrivateImages}
                        onChange={e => setRankForm({ ...rankForm, canSendPrivateImages: e.target.checked })}
                      />
                      Allow private chat images
                    </label>
                  </div>
                  <div className="rank-actions">
                    <button className="update-btn" onClick={handleSaveRankDefinition}>
                      {editingRankId ? 'Update Rank' : 'Create Rank'}
                    </button>
                    {editingRankId && (
                      <button className="cancel-btn" onClick={() => resetRankForm()}>
                        Cancel
                      </button>
                    )}
                  </div>
                  {rankMessage && <p className="rank-message">{rankMessage}</p>}

                  <div className="rank-list">
                    {availableRanks.filter(r => !r.isDefault).length === 0 ? (
                      <p>No custom ranks yet.</p>
                    ) : (
                      availableRanks
                        .filter(r => !r.isDefault)
                        .map(rank => (
                          <div key={rank.id} className="rank-item">
                            <div>
                              <strong>{rank.name}</strong>
                              <small className="rank-id" title="Rank Identifier">/{rank.id}</small>
                              <span className="rank-permission-pill">
                                {rank.permissions?.canSendWorldImages ? 'World images' : 'Text only'}
                              </span>
                              <span className="rank-permission-pill">
                                {rank.permissions?.canSendPrivateImages !== false
                                  ? 'Private images'
                                  : 'Private text'}
                              </span>
                            </div>
                            <div className="rank-item-actions">
                              <button className="edit-btn" onClick={() => handleEditRank(rank)} type="button">
                                Edit
                              </button>
                              <button className="delete-btn" onClick={() => handleDeleteRank(rank.id)} type="button">
                                Delete
                              </button>
                            </div>
                          </div>
                        ))
                    )}
                  </div>
                </div>
              )}

              {/* Password Management */}
              {canChangePasswords() && (
                <div className="password-management">
                  <h4>Change Password</h4>
                  {!showPasswordChange ? (
                    <button className="update-btn" type="button" onClick={() => setShowPasswordChange(true)}>
                      Change Password
                    </button>
                  ) : (
                    <div className="form-group">
                      <input
                        type="password"
                        placeholder="New password (min 6 characters)"
                        value={newPassword}
                        onChange={e => setNewPassword(e.target.value)}
                        minLength={6}
                      />
                      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                        <button
                          className="update-btn"
                          onClick={handleChangePassword}
                          type="button"
                          disabled={!newPassword || newPassword.length < 6}
                        >
                          Save Password
                        </button>
                        <button
                          className="cancel-btn"
                          onClick={() => { setShowPasswordChange(false); setNewPassword(''); }}
                          type="button"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Account Actions */}
              <div className="account-actions">
                <h4>Account Actions</h4>
                <div className="action-buttons">
                  {selectedUser.locked ? (
                    <button
                      className="unlock-btn"
                      onClick={handleUnlockAccount}
                      type="button"
                    >
                      🔓 Unlock Account
                    </button>
                  ) : (
                    <button
                      className="lock-btn"
                      onClick={handleLockAccount}
                      type="button"
                    >
                      🔒 Lock Account
                    </button>
                  )}
                  <button
                    className="delete-btn"
                    onClick={handleDeleteAccount}
                    type="button"
                  >
                    🗑️ Delete Account
                  </button>
                </div>
              </div>

              {/* Entry Management */}
              <div className="entry-management">
                <h4>Manage Entries</h4>
                <div className="form-group">
                  <label>Giveaway:</label>
                  <select
                    value={entryData.giveawayId}
                    onChange={e => setEntryData({ ...entryData, giveawayId: e.target.value })}
                  >
                    <option value="">Select giveaway...</option>
                    {giveaways.map(g => (
                      <option key={g.id} value={g.id.toString()}>
                        {g.title} (ID: {g.id})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Entries:</label>
                  <input
                    type="number"
                    min={1}
                    value={entryData.entries}
                    onChange={e => setEntryData({
                      ...entryData,
                      entries: Math.max(1, parseInt(e.target.value, 10) || 1)
                    })}
                  />
                </div>
                <div className="form-group">
                  <label>Duration (days):</label>
                  <input
                    type="number"
                    min={1}
                    value={entryData.duration}
                    onChange={e => setEntryData({
                      ...entryData,
                      duration: Math.max(1, parseInt(e.target.value, 10) || 1),
                    })}
                  />
                </div>
                <button className="update-btn" onClick={handleUpdateEntries} type="button">
                  Update Entries
                </button>

                <div className="current-entries">
                  <h5>Current Entries:</h5>
                  {selectedUser.entries &&
                  Object.keys(selectedUser.entries).length > 0 ? (
                    Object.entries(selectedUser.entries).map(([giveawayId, data]) => {
                      const giveaway = giveaways.find(g => g.id.toString() === giveawayId);
                      const expiresAt = data.expiresAt ? new Date(data.expiresAt) : null;
                      const isExpired = expiresAt ? expiresAt < new Date() : false;
                      return (
                        <div key={giveawayId} className={`entry-item ${isExpired ? 'expired' : ''}`}>
                          <strong>
                            {giveaway?.title ? giveaway.title : `Giveaway ${giveawayId}`}
                          </strong>
                          <span>{data.count} entries</span>
                          {expiresAt && (
                            <small>
                              Expires: {expiresAt.toLocaleDateString()} {isExpired && '(Expired)'}
                            </small>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <p>No entries assigned</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminPanel;
